import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {CurrencyService, InvoiceOrderService, PartnerService} from '@app/core';
import {ActivatedRoute} from '@angular/router';
import {Currency, GetParams, InvoiceOrder, Partner, PurchaseOrder, Service, ServiceOrder, SharedModule} from '@app/shared';
import {CommonModule, DecimalPipe} from '@angular/common';
import {NzCellAlignDirective, NzTableComponent} from 'ng-zorro-antd/table';
import {NzDescriptionsComponent, NzDescriptionsItemComponent} from 'ng-zorro-antd/descriptions';
import {NzColDirective, NzRowDirective} from 'ng-zorro-antd/grid';
import {environment} from '../../../../../environments/environment';
import {forkJoin} from 'rxjs';

interface GroupedService {
  service: Service;
  serviceOrders: {
    serviceOrder: ServiceOrder;
    totalAmount: number;
    totalAmountUSD: number;
    dinamicData: any;
  }[];
  totalServiceAmount: number;
  totalServiceAmountUSD: number;
  serviceIva: number;
  percentIva: number; // Nueva propiedad
  serviceDinamicData?: any;
  displayIndex?: number;
}

interface FlattenedServiceOrder {
  service: Service;
  serviceOrder: ServiceOrder;
  totalAmount: number;
  totalAmountUSD: number;
  dinamicData: any;
}

interface SummaryChunk {
  rows: GroupedService[];
  includeTotals: boolean;
}

interface OperationsChunk {
  rows: FlattenedServiceOrder[];
  includeTotalsSummary: boolean;
}

interface PdfPage {
  summary?: SummaryChunk;
  operations?: OperationsChunk;
  showStaticTop: boolean;
  showSignature: boolean;
}

interface LayoutMetrics {
  contentHeight: number;
  staticSectionHeight: number;
  summaryHeaderHeight: number;
  summaryRowHeights: number[];
  summaryRowFallbackHeight: number;
  summaryTotalsHeight: number;
  operationsHeaderWithTotalsHeight: number;
  operationsHeaderWithoutTotalsHeight: number;
  operationsRowHeights: number[];
  operationsRowFallbackHeight: number;
  signatureHeight: number;
  footerHeight: number;
  contentGap: number;
}

interface InternalPageState {
  page: PdfPage;
  remainingHeight: number;
  blocksInContent: number;
  summaryState?: {
    chunk: SummaryChunk;
  };
  operationsState?: {
    chunk: OperationsChunk;
    includeTotalsSummary: boolean;
  };
}

@Component({
  selector: 'app-pdf',
  templateUrl: './pdf.component.html',
  imports: [
    CommonModule,
    DecimalPipe,
    NzTableComponent,
    NzDescriptionsComponent,
    NzDescriptionsItemComponent,
    SharedModule,
    NzCellAlignDirective,
    NzRowDirective,
    NzColDirective,
  ],
  styleUrls: ['./pdf.component.less']
})
export class PdfComponent implements OnInit, AfterViewChecked, AfterViewInit {

  orderId: string = '';
  clientId: string = '';
  currencyId: string = '';
  order!: InvoiceOrder | PurchaseOrder;
  client!: Partner;
  isInvoiceOrder: boolean = true;

  groupedServices: GroupedService[] = [];

  isLoading = true;
  errorMessage: string | null = null;

  pages: PdfPage[] = [];

  private layoutMetrics: LayoutMetrics | null = null;
  private pendingPagination = false;
  private paginationRetryCount = 0;
  private operationsRows: FlattenedServiceOrder[] = [];

  totalRow: any = {
    id: 'Totales',
    totalIva: 0,
    percent: '100%',
    detail: 0,
    usd: 0,
    amount: 0,
    totalDinamicData: {}
  };

  isPdfReady = false;
  logoLoaded = false;
  accessToken: any;
  respuestaPeticion: any;
  respuestaPeticionPartner: any;
  protected readonly environment = environment;
  private viewChecked = false;
  private _pageContentRefs: QueryList<ElementRef<HTMLElement>> | null = null;
  private _summaryTableRefs: QueryList<ElementRef<HTMLElement>> | null = null;
  private _summaryInlineTableRef: ElementRef<HTMLElement> | null = null;
  private _staticContentAreaRef: ElementRef<HTMLElement> | null = null;
  private _summarySectionContainerRef: ElementRef<HTMLElement> | null = null;
  private _operationsSectionContainerRef: ElementRef<HTMLElement> | null = null;
  private _operationsSummaryRowRef: ElementRef<HTMLElement> | null = null;
  private _signatureSectionRef: ElementRef<HTMLElement> | null = null;
  private _footerRef: ElementRef<HTMLElement> | null = null;
  private readonly defaultRowHeight = 32;
  private orderDataReady = false;
  private clientDataReady = false;

  currentCurrency: Currency | undefined;

  _dinamicCurrencyColumns = new Map<number, { id: number, name: string }>();
  get dinamicCurrencyColumns(): { id: number, name: string }[] {
    return Array.from(this._dinamicCurrencyColumns.values());
  }

  constructor(private invoiceOrderService: InvoiceOrderService,
              private route: ActivatedRoute,
              private partnerService: PartnerService,
              private currencyService: CurrencyService,
              private cdr: ChangeDetectorRef,
  ) {
  }

  @ViewChildren('pageContent', { read: ElementRef })
  set pageContentElements(refs: QueryList<ElementRef<HTMLElement>>) {
    this._pageContentRefs = refs;
  }

  @ViewChildren('summaryTable', { read: ElementRef })
  set summaryTableRefs(refs: QueryList<ElementRef<HTMLElement>>) {
    this._summaryTableRefs = refs;
    this.checkTables();
  }

  @ViewChild('summaryInlineTable', { read: ElementRef })
  set summaryInlineTable(ref: ElementRef<HTMLElement>) {
    this._summaryInlineTableRef = ref;
    this.checkTables();
  }

  @ViewChild('staticContentArea', { read: ElementRef })
  set staticContentArea(ref: ElementRef<HTMLElement>) {
    this._staticContentAreaRef = ref;
  }

  @ViewChild('summarySectionContainer', { read: ElementRef })
  set summarySectionContainer(ref: ElementRef<HTMLElement>) {
    this._summarySectionContainerRef = ref;
  }

  @ViewChild('operationsSectionContainer', { read: ElementRef })
  set operationsSectionContainer(ref: ElementRef<HTMLElement>) {
    this._operationsSectionContainerRef = ref;
  }

  @ViewChild('operationsSummaryRow', { read: ElementRef })
  set operationsSummaryRow(ref: ElementRef<HTMLElement>) {
    this._operationsSummaryRowRef = ref;
  }

  @ViewChild('signatureSection', { read: ElementRef })
  set signatureSection(ref: ElementRef<HTMLElement>) {
    this._signatureSectionRef = ref;
  }

  @ViewChild('pageFooter', { read: ElementRef })
  set footer(ref: ElementRef<HTMLElement>) {
    this._footerRef = ref;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.isInvoiceOrder = this.route.snapshot.url.some(segment => segment.path === 'estado-pago');
      this.orderId = params['id'];
      this.clientId = params['clientId']
      this.currencyId = params['currencyId']

      // Obtener el valor de la cookie 'jwt'
      const getCookie = (name: any) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()!.split(';').shift();
        else return null;
      };

      // Obtener el valor de la cookie 'jwt'
      this.accessToken = getCookie('jwt');

      this.loadOrder();
    });
  }

  ngAfterViewChecked(): void {
    if (!this.isLoading && !this.viewChecked) {
      if (this.logoLoaded && this.client) {
        this.markAsReady();
      }
    }
  }

  onLogoLoaded(): void {
    this.logoLoaded = true;

    if (!this.isLoading && !this.viewChecked && this.client) {
      this.markAsReady();
    }
  }

  isInvoice(order: InvoiceOrder | PurchaseOrder): order is InvoiceOrder {
    return this.isInvoiceOrder;
  }

  isPurchase(order: InvoiceOrder | PurchaseOrder): order is PurchaseOrder {
    return !this.isInvoiceOrder;
  }

  groupServices() {
    const serviceMap = new Map<number, {
      service: Service;
      serviceOrders: Map<number, {
        serviceOrder: ServiceOrder;
        totalAmount: number;
        totalAmountUSD: number;
        dinamicData: any;
      }>;
      totalServiceAmount: number;
      totalServiceAmountUSD: number;
      percentIva: number;
      serviceIva: number;
      serviceDinamicData?: any;
    }>();

    const orderCurrencyExchange =  this.currentCurrency?.currentCurrencyExchange?.baseRate || 1;
    this.order?.analyticOperations?.forEach(operation => {

      // Filtrar los datos que pertenecen al Partner
      if (this.isInvoiceOrder) {
        if (operation.serviceOrder?.service?.project?.partnerId?.toString() !== this.clientId) {
          return;
        }
      } else {
        if (operation.serviceOrder!.service!.supplierId!.toString() !== this.clientId) {
          return;
        }
      }

      let currentDinamicData: any = {};

      if (operation.currency) {
        this._dinamicCurrencyColumns.set(operation.currency.id, { id: operation.currency.id, name: operation.currency.isoCode });
        currentDinamicData[`currency_${operation.currency.id}`] = (currentDinamicData[`currency_${operation.currency.id}`] || 0) + operation.amount;
      }

      const serviceOrder = operation.serviceOrder;
      if (!serviceOrder || !serviceOrder.service) return;

      const service = serviceOrder.service;
      const serviceId = service.id;
      const serviceOrderId = serviceOrder.id;

      let amountUSD = 0;
      const operationCurrency = operation.currency;
      const operationCurrencyExchange = operationCurrency?.currentCurrencyExchange?.baseRate || 1;

      if (operationCurrency?.id === this.currentCurrency?.id) {
        amountUSD = (operation.amount || 0) / orderCurrencyExchange;
      } else {
        amountUSD = (operation.amount || 0) / operationCurrencyExchange;
      }

      let amountInOrderCurrency = operation.amount || 0;
      if (operationCurrency?.id !== this.currentCurrency?.id) {
        amountInOrderCurrency = operation.amount * (orderCurrencyExchange / operationCurrencyExchange);
      }

      if (!serviceMap.has(serviceId)) {
        serviceMap.set(serviceId, {
          service: service,
          serviceOrders: new Map(),
          totalServiceAmount: 0,
          totalServiceAmountUSD: 0,
          percentIva: 0,
          serviceIva: 0,
          serviceDinamicData: {}
        });
      }

      const serviceGroup = serviceMap.get(serviceId)!;

      if (!serviceGroup.serviceOrders.has(serviceOrderId)) {
        const { service: _, ...cleanServiceOrder } = serviceOrder;
        serviceGroup.serviceOrders.set(serviceOrderId, {
          serviceOrder: cleanServiceOrder,
          totalAmount: 0,
          totalAmountUSD: 0,
          dinamicData: {}
        });
      }

      const serviceOrderGroup = serviceGroup.serviceOrders.get(serviceOrderId)!;

      serviceOrderGroup.totalAmount += amountInOrderCurrency;
      serviceOrderGroup.totalAmountUSD += amountUSD;
      serviceOrderGroup.dinamicData = {
        ...serviceOrderGroup.dinamicData,
        ...Object.keys(currentDinamicData).reduce((acc, key) => {
          // Si la propiedad ya existe en dinamicData, sumamos los valores, si no la agregamos
          acc[key] = (serviceOrderGroup.dinamicData[key] || 0) + (currentDinamicData[key] || 0);
          return acc;
        }, {} as any) // Creamos un objeto acumulador vacío
      };

      serviceGroup.totalServiceAmount += amountInOrderCurrency;
      serviceGroup.totalServiceAmountUSD += amountUSD;

      // Acumular valores de dinamicData para el servicio
      serviceGroup.serviceDinamicData = {
        ...serviceGroup.serviceDinamicData,
        ...Object.keys(currentDinamicData).reduce((acc, key) => {
          acc[key] = (serviceGroup.serviceDinamicData[key] || 0) + (currentDinamicData[key] || 0);
          return acc;
        }, {} as any)
      };
    });

    serviceMap.forEach(serviceGroup => {
      const service = serviceGroup.service;
      if (this.isInvoiceOrder) {
        serviceGroup.serviceIva = service.servicePricing?.unitSaleVat
          ? service.servicePricing.unitSaleVat : (service.servicePricing?.totalSaleVat ?? 0);
      } else {
        serviceGroup.serviceIva = service.servicePricing?.unitPurchaseVat
          ? service.servicePricing.unitPurchaseVat : (service.servicePricing?.totalPurchaseVat ?? 0);
      }
    });

    this.groupedServices = Array.from(serviceMap.values()).map(serviceGroup => ({
      service: serviceGroup.service,
      serviceOrders: Array.from(serviceGroup.serviceOrders.values()),
      totalServiceAmount: serviceGroup.totalServiceAmount,
      totalServiceAmountUSD: serviceGroup.totalServiceAmountUSD,
      serviceIva: serviceGroup.serviceIva,
      percentIva: 0,
      serviceDinamicData: serviceGroup.serviceDinamicData // Asignar los valores dinámicos acumulados
    }));

    // this.totalRow.totalIva = this.groupedServices?.reduce((sum, g) => sum + g.serviceIva, 0) || 0;

    serviceMap.forEach(serviceGroup => {
      serviceGroup.percentIva = this.totalRow.totalIva > 0
        ? (serviceGroup.serviceIva / this.totalRow.totalIva) * 100
        : 0;
    });

    this.groupedServices = Array.from(serviceMap.values()).map((serviceGroup, index) => ({
      service: serviceGroup.service,
      serviceOrders: Array.from(serviceGroup.serviceOrders.values()),
      totalServiceAmount: serviceGroup.totalServiceAmount,
      totalServiceAmountUSD: serviceGroup.totalServiceAmountUSD,
      serviceIva: serviceGroup.serviceIva,
      percentIva: serviceGroup.percentIva,
      serviceDinamicData: serviceGroup.serviceDinamicData, // Incluir los datos dinámicos finales
      displayIndex: index + 1
    }));

    this.totalRow.amount = this.groupedServices?.reduce((sum, g) => sum + g.totalServiceAmount, 0)
    this.totalRow.totalIva = this.groupedServices?.reduce((sum, g) => sum + (g.totalServiceAmount * g.serviceIva / 100), 0) || 0;
    this.totalRow.detail = this.groupedServices?.reduce((sum, g) => sum + g.serviceOrders.length, 0)

    this.totalRow.usd = this.groupedServices?.reduce((sum, g) => sum + g.totalServiceAmountUSD, 0) || 0;

    // Inicializa el objeto totalDinamicData como vacío
    let totalDinamicData: any = {};

// Recorre los groupedServices y acumula los valores de serviceDinamicData
    this.groupedServices?.forEach(serviceGroup => {
      // Recorre las claves del serviceDinamicData de cada serviceGroup
      Object.keys(serviceGroup.serviceDinamicData).forEach(key => {
        // Si la clave ya existe en totalDinamicData, sumamos el valor, sino la inicializamos
        totalDinamicData[key] = (totalDinamicData[key] || 0) + (serviceGroup.serviceDinamicData[key] || 0);
      });
    });

// Asigna totalDinamicData a totalRow
    this.totalRow.totalDinamicData = totalDinamicData;
  }

  loadOrder(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.orderDataReady = false;
    this.clientDataReady = false;

    const getParams: GetParams = {
      startRow: 0,
      endRow: 1,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: this.orderId
    };

    const currencyParams: GetParams = {
      startRow: 0,
      endRow: 1,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: this.currencyId
    };

    // Determinar la petición de orden según el tipo
    const orderRequest = this.isInvoiceOrder
      ? this.invoiceOrderService.getInvoiceOrder(getParams, this.accessToken)
      : this.invoiceOrderService.getPurchaseOrder(getParams, this.accessToken);

    // Configurar las peticiones paralelas
    const requests = {
      currency: this.currencyService.getCurrency(currencyParams, this.accessToken),
      order: orderRequest
    };

    forkJoin(requests).subscribe({
      next: ({ currency, order }) => {

        if (order.data?.rows?.length > 0) {
          this.order = this.isInvoiceOrder
            ? (order.data.rows[0] as InvoiceOrder)
            : (order.data.rows[0] as PurchaseOrder);

          if (currency.data?.rows?.length > 0) {
            this.currentCurrency = currency.data.rows[0];
          } else {
            this.currentCurrency = this.order.currency;
          }

          this.processData();
        } else {
          // Manejar caso de orden vacía
          this.errorMessage = this.isInvoiceOrder
            ? 'No se encontró la orden de factura.'
            : 'No se encontró El estado de pago.';
        }

        this.isLoading = false;
      },
      error: (err) => {
        // Manejar caso de orden vacía
        this.errorMessage = this.isInvoiceOrder
          ? 'Error al obtener la orden de factura.'
          : 'Error al obtener el estado de pago.';
        this.isLoading = false;
        console.error('Error loading data', err);
      }
    });


    getParams.filterValue = this.clientId
    this.partnerService.getPartner(getParams, this.accessToken).subscribe({
      next: (response) => {
        this.respuestaPeticionPartner = response
        if (response.data && response.data.rows.length > 0) {
          this.client = response.data.rows[0];
        } else {
          this.errorMessage = 'No se encontró el cliente.';
        }
        this.isLoading = false;
        this.clientDataReady = true;
        this.tryInitializePagination();
      },
      error: (err) => {
        this.respuestaPeticionPartner = err
        this.errorMessage = 'Error al cargar los datos del cliente.';
        this.isLoading = false;
        console.error('Error loading client', err);
        this.clientDataReady = true;
        this.tryInitializePagination();
      }
    });
  }

  ngAfterViewInit() {
    this.checkTables();
  }

  checkTables() {
    const summaryTableRef = this._summaryTableRefs?.last;
    if (summaryTableRef && this._summaryInlineTableRef) {
      setTimeout(() => this.syncColumnWidths(summaryTableRef.nativeElement, this._summaryInlineTableRef!.nativeElement), 0);
    }
  }

  private syncColumnWidths(mainTableEl: HTMLElement, inlineTableEl: HTMLElement) {
    if (!mainTableEl || !inlineTableEl) {
      return;
    }

    const mainHeaders = mainTableEl.querySelectorAll('thead tr:first-child th') as NodeListOf<HTMLElement>;
    const inlineHeaders = inlineTableEl.querySelectorAll('tr:first-child th, tr:first-child td') as NodeListOf<HTMLElement>;
    const inlineRows = inlineTableEl.querySelectorAll('tbody tr') as NodeListOf<HTMLTableRowElement>;

    Array.from(mainHeaders).forEach((header, index) => {
      if (inlineHeaders[index]) {
        const width = `${header.offsetWidth}px`;

        inlineHeaders[index].style.width = width;
        inlineHeaders[index].style.minWidth = width;

        Array.from(inlineRows).forEach(row => {
          const cell = row.cells?.[index] as HTMLElement | undefined;
          if (cell) {
            cell.style.width = width;
            cell.style.minWidth = width;
          }
        });
      }
    });
  }

  private markAsReady(): void {
    setTimeout(() => {
      this.isPdfReady = true;
      this.viewChecked = true;
    }, 500);
  }

  ////////////Paginado//////////

  private processData() {
    if (!this.order) {
      return;
    }

    if (this.order.analyticOperations) {
      this.groupServices();
    }

    this.operationsRows = this.buildOperationRows();
    this.orderDataReady = true;
    this.tryInitializePagination();
  }

  private buildOperationRows(): FlattenedServiceOrder[] {
    const allServiceOrders: FlattenedServiceOrder[] = [];
    this.groupedServices?.forEach(group => {
      group.serviceOrders.forEach(serviceOrder => {
        allServiceOrders.push({
          service: group.service,
          serviceOrder: serviceOrder.serviceOrder,
          totalAmount: serviceOrder.totalAmount,
          totalAmountUSD: serviceOrder.totalAmountUSD,
          dinamicData: serviceOrder.dinamicData
        });
      });
    });
    return allServiceOrders;
  }

  private initializePagination(): void {
    this.pendingPagination = true;
    this.paginationRetryCount = 0;

    const initialPage: PdfPage = {
      showStaticTop: true,
      showSignature: true
    };

    if (this.groupedServices.length) {
      initialPage.summary = {
        rows: [...this.groupedServices],
        includeTotals: true
      };
    }

    if (this.operationsRows.length) {
      initialPage.operations = {
        rows: [...this.operationsRows],
        includeTotalsSummary: true
      };
    }

    this.pages = [initialPage];

    setTimeout(() => this.applyDynamicPagination());
  }

  private tryInitializePagination(): void {
    if (!this.orderDataReady) {
      return;
    }

    if (!this.clientDataReady) {
      return;
    }

    this.initializePagination();
  }

  private applyDynamicPagination(): void {
    if (!this.pendingPagination) {
      return;
    }

    const metrics = this.collectLayoutMetrics();

    if (!metrics) {
      if (this.paginationRetryCount < 5) {
        this.paginationRetryCount += 1;
        setTimeout(() => this.applyDynamicPagination(), 50);
      } else {
        this.pendingPagination = false;
      }
      return;
    }

    this.layoutMetrics = metrics;
    const pages = this.buildPaginatedPages(metrics);
    this.pages = pages;
    this.pendingPagination = false;
    this.cdr.detectChanges();
  }

  private collectLayoutMetrics(): LayoutMetrics | null {
    const contentEl = this._pageContentRefs?.first?.nativeElement;
    if (!contentEl) {
      return null;
    }

    const staticContentEl = this._staticContentAreaRef?.nativeElement;
    const summaryContainerEl = this._summarySectionContainerRef?.nativeElement;
    const summaryTableEl = this._summaryTableRefs?.first?.nativeElement;
    const summaryTotalsEl = this._summaryInlineTableRef?.nativeElement;
    const operationsContainerEl = this._operationsSectionContainerRef?.nativeElement;
    const operationsSummaryRowEl = this._operationsSummaryRowRef?.nativeElement;
    const signatureEl = this._signatureSectionRef?.nativeElement;
    const footerEl = this._footerRef?.nativeElement;

    if (!signatureEl || !footerEl) {
      return null;
    }

    const contentRect = contentEl.getBoundingClientRect();
    const contentGap = typeof window !== 'undefined'
      ? parseFloat(window.getComputedStyle(contentEl).rowGap || '0') || 0
      : 24;

    const hasSummary = this.groupedServices.length > 0;
    const hasOperations = this.operationsRows.length > 0;

    let staticSectionHeight = 0;
    if (staticContentEl) {
      staticSectionHeight = this.measureBlockHeight(staticContentEl);
    }

    let summaryRowHeights: number[] = [];
    let summaryTotalsHeight = 0;
    let summaryHeaderHeight = 0;

    if (hasSummary) {
      if (!summaryContainerEl || !summaryTableEl || !summaryTotalsEl) {
        return null;
      }

      const summaryRows = summaryTableEl.querySelectorAll('tbody tr');
      summaryRowHeights = Array.from(summaryRows).map(row => this.measureRowHeight(row as HTMLElement));
      if (!summaryRowHeights.length || summaryRowHeights.some(height => height < 1)) {
        return null;
      }
      summaryTotalsHeight = summaryTotalsEl ? this.measureBlockHeight(summaryTotalsEl) : 0;
      const summaryContainerHeight = this.measureBlockHeight(summaryContainerEl);
      const totalSummaryRowsHeight = summaryRowHeights.reduce((acc, height) => acc + height, 0);
      summaryHeaderHeight = Math.max(0, summaryContainerHeight - totalSummaryRowsHeight - summaryTotalsHeight);
    }

    let operationsRowHeights: number[] = [];
    let operationsHeaderWithTotalsHeight = 0;
    let operationsHeaderWithoutTotalsHeight = 0;

    if (hasOperations) {
      if (!operationsContainerEl) {
        return null;
      }

      const operationsTableEl = operationsContainerEl.querySelector('table');
      const operationsRows = operationsTableEl?.querySelectorAll('tbody tr');
      if (operationsTableEl && operationsRows && operationsRows.length > 0) {
        operationsRowHeights = Array.from(operationsRows).map(row => this.measureRowHeight(row as HTMLElement));
        if (!operationsRowHeights.length || operationsRowHeights.some(height => height < 1)) {
          return null;
        }
        const totalOperationsRowsHeight = operationsRowHeights.reduce((acc, height) => acc + height, 0);
        const operationsContainerHeight = this.measureBlockHeight(operationsContainerEl);
        operationsHeaderWithTotalsHeight = Math.max(0, operationsContainerHeight - totalOperationsRowsHeight);
        const operationsSummaryHeight = this.measureBlockHeight(operationsSummaryRowEl ?? null);
        operationsHeaderWithoutTotalsHeight = Math.max(0, operationsHeaderWithTotalsHeight - operationsSummaryHeight);
      }
    }

    const signatureHeight = signatureEl.getBoundingClientRect().height;
    const footerHeight = footerEl.getBoundingClientRect().height;

    return {
      contentHeight: contentRect.height,
      staticSectionHeight,
      summaryHeaderHeight: summaryHeaderHeight,
      summaryRowHeights,
      summaryRowFallbackHeight: summaryRowHeights.find(height => height > 0) ?? this.defaultRowHeight,
      summaryTotalsHeight: summaryTotalsHeight,
      operationsHeaderWithTotalsHeight,
      operationsHeaderWithoutTotalsHeight,
      operationsRowHeights,
      operationsRowFallbackHeight: operationsRowHeights.find(height => height > 0) ?? this.defaultRowHeight,
      signatureHeight,
      footerHeight,
      contentGap
    };
  }

  private buildPaginatedPages(metrics: LayoutMetrics): PdfPage[] {
    const states: InternalPageState[] = [];
    let currentState = this.startNewPage(states, metrics);

    const summaryRowHeights = metrics.summaryRowHeights;
    const summaryFallbackHeight = metrics.summaryRowFallbackHeight;
    const operationsRowHeights = metrics.operationsRowHeights;
    const operationsFallbackHeight = metrics.operationsRowFallbackHeight;

    const summaryQueue = this.groupedServices.map((group, index) => ({
      data: group,
      height: summaryRowHeights[index] ?? summaryFallbackHeight
    }));

    const operationsQueue = this.operationsRows.map((row, index) => ({
      data: row,
      height: operationsRowHeights[index] ?? operationsFallbackHeight
    }));

    const hasSummary = summaryQueue.length > 0;
    const summaryRequiresTotals = hasSummary;

    while (summaryQueue.length > 0) {
      const isLastRow = summaryQueue.length === 1;
      const nextItem = summaryQueue[0];
      const rowHeight = Math.max(nextItem.height, metrics.summaryRowFallbackHeight);
      const requiredSpace = rowHeight + (isLastRow && summaryRequiresTotals ? metrics.summaryTotalsHeight : 0);
      currentState = this.ensureSummaryChunk(currentState, metrics, states, requiredSpace);

      const item = summaryQueue.shift()!;
      currentState.summaryState!.chunk.rows.push(item.data);
      currentState.remainingHeight = Math.max(0, currentState.remainingHeight - rowHeight);
    }

    if (summaryRequiresTotals) {
      currentState = this.placeSummaryTotals(currentState, metrics, states);
    }

    let operationsStarted = false;

    while (operationsQueue.length > 0) {
      const nextItem = operationsQueue[0];
      const rowHeight = Math.max(nextItem.height, metrics.operationsRowFallbackHeight);
      const requiredSpace = rowHeight;
      currentState = this.ensureOperationsChunk(currentState, metrics, states, operationsStarted, requiredSpace);

      const item = operationsQueue.shift()!;
      currentState.operationsState!.chunk.rows.push(item.data);
      currentState.remainingHeight = Math.max(0, currentState.remainingHeight - rowHeight);
      operationsStarted = true;
    }

    if (states.length) {
      const lastState = states[states.length - 1];
      if (metrics.signatureHeight > 0 && lastState.remainingHeight < metrics.signatureHeight) {
        lastState.page.showSignature = false;
        const signatureState = this.startNewPage(states, metrics);
        signatureState.page.showSignature = true;
      }
    }

    const pages = states.map((state, index) => ({
      ...state.page,
      showStaticTop: index === 0,
      showSignature: state.page.showSignature
    }));

    if (!pages.length) {
      pages.push({
        showStaticTop: true,
        showSignature: true
      });
    }

    if (!pages.some(page => page.showSignature)) {
      pages[pages.length - 1].showSignature = true;
    }

    return pages;
  }

  private startNewPage(states: InternalPageState[], metrics: LayoutMetrics): InternalPageState {
    const isFirstPage = states.length === 0;
    const page: PdfPage = {
      showStaticTop: isFirstPage,
      showSignature: false
    };

    let remainingHeight = metrics.contentHeight;
    let blocksInContent = 0;

    if (isFirstPage && metrics.staticSectionHeight > 0) {
      remainingHeight -= metrics.staticSectionHeight;
      blocksInContent = 1;
    }

    if (remainingHeight < 0) {
      remainingHeight = 0;
    }

    const state: InternalPageState = {
      page,
      remainingHeight,
      blocksInContent
    };

    states.push(state);
    return state;
  }

  private ensureSummaryChunk(currentState: InternalPageState, metrics: LayoutMetrics, states: InternalPageState[], requiredSpace: number): InternalPageState {
    let state = currentState;

    while (true) {
      const needsGap = !state.summaryState && state.blocksInContent > 0 && metrics.contentGap > 0;
      const gapSpace = needsGap ? metrics.contentGap : 0;
      const headerSpace = !state.summaryState ? metrics.summaryHeaderHeight : 0;

      if (state.remainingHeight >= gapSpace + headerSpace + requiredSpace) {
        if (!state.summaryState) {
          if (gapSpace > 0) {
            state.remainingHeight = Math.max(0, state.remainingHeight - gapSpace);
          }

          const chunk: SummaryChunk = {
            rows: [],
            includeTotals: false
          };

          state.page.summary = chunk;
          state.summaryState = { chunk };
          state.remainingHeight = Math.max(0, state.remainingHeight - metrics.summaryHeaderHeight);
          state.blocksInContent += 1;
        }
        return state;
      }

      state = this.startNewPage(states, metrics);
    }
  }

  private placeSummaryTotals(currentState: InternalPageState, metrics: LayoutMetrics, states: InternalPageState[]): InternalPageState {
    let state = currentState;

    while (true) {
      state = this.ensureSummaryChunk(state, metrics, states, metrics.summaryTotalsHeight);

      if (state.remainingHeight >= metrics.summaryTotalsHeight) {
        state.summaryState!.chunk.includeTotals = true;
        state.remainingHeight = Math.max(0, state.remainingHeight - metrics.summaryTotalsHeight);
        return state;
      }

      state = this.startNewPage(states, metrics);
    }
  }

  private ensureOperationsChunk(currentState: InternalPageState, metrics: LayoutMetrics, states: InternalPageState[], operationsStarted: boolean, requiredSpace: number): InternalPageState {
    let state = currentState;

    while (true) {
      const needsGap = !state.operationsState && state.blocksInContent > 0 && metrics.contentGap > 0;
      const gapSpace = needsGap ? metrics.contentGap : 0;
      const headerHeight = !state.operationsState
        ? (operationsStarted ? metrics.operationsHeaderWithoutTotalsHeight : metrics.operationsHeaderWithTotalsHeight)
        : 0;

      if (state.remainingHeight >= gapSpace + headerHeight + requiredSpace) {
        if (!state.operationsState) {
          if (gapSpace > 0) {
            state.remainingHeight = Math.max(0, state.remainingHeight - gapSpace);
          }

          const chunk: OperationsChunk = {
            rows: [],
            includeTotalsSummary: !operationsStarted
          };

          state.page.operations = chunk;
          state.operationsState = {
            chunk,
            includeTotalsSummary: !operationsStarted
          };
          state.remainingHeight = Math.max(0, state.remainingHeight - headerHeight);
          state.blocksInContent += 1;
        }

        return state;
      }

      state = this.startNewPage(states, metrics);
    }
  }

  private measureBlockHeight(element: HTMLElement | null): number {
    if (!element) {
      return 0;
    }

    let height = element.getBoundingClientRect().height;

    if (!height || height < 1) {
      height = element.offsetHeight;
    }

    if ((!height || height < 1) && typeof window !== 'undefined') {
      const styles = window.getComputedStyle(element);
      const marginTop = parseFloat(styles.marginTop) || 0;
      const marginBottom = parseFloat(styles.marginBottom) || 0;
      const computedHeight = parseFloat(styles.height) || 0;
      height = computedHeight + marginTop + marginBottom;
    }

    return height;
  }

  private measureRowHeight(element: HTMLElement | null): number {
    if (!element) {
      return this.defaultRowHeight;
    }

    let height = element.getBoundingClientRect().height;

    if (!height || height < 1) {
      height = element.offsetHeight;
    }

    if ((!height || height < 1) && typeof window !== 'undefined') {
      const styles = window.getComputedStyle(element);
      const marginTop = parseFloat(styles.marginTop) || 0;
      const marginBottom = parseFloat(styles.marginBottom) || 0;
      const computedHeight = parseFloat(styles.height) || 0;
      height = computedHeight + marginTop + marginBottom;
    }

    if (!height || height < 1) {
      height = this.defaultRowHeight;
    }

    return height;
  }

  isNoDecimalCurrency() {
    const currenciesWithNoDecimalParts = ['CLP', 'USD'];
    return currenciesWithNoDecimalParts.includes(this.currentCurrency?.isoCode || '0');
  }

  getCurrentDateFormatted(): string {
    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = now.toLocaleString('es-ES', { month: 'short' }).toUpperCase();
    const year = now.getFullYear();
    return `${day} ${month} ${year}`;
  }

  getTotalServiceOrders(): number {
    return this.groupedServices?.reduce((sum, group) => sum + group.serviceOrders.length, 0) || 0;
  }

  getTotalAmount(): number {
    return this.groupedServices?.reduce((sum, group) => sum + group.totalServiceAmount, 0) || 0;
  }

  getAllServiceOrders(): any[] {
    return this.operationsRows;
  }
}
